import React, {
  createContext,
  useReducer,
  useCallback,
  useEffect,
} from "react";
import api from "../services/api";
import storage from "../services/storage";
import encryption from "../services/encryption";
import { useAuth } from "./AuthContext";
import { useNotification } from "../hooks/useNotification";

const UISettingsContext = createContext();

const initialState = {
  // Modal states
  isPrivateKeyModalOpen: false,
  isTwoFAModalOpen: false,
  isContactModalOpen: false,
  isPinModalOpen: false,
  isSelfDestructModalOpen: false,
  isNewTokenModalOpen: false,
  isRecoveryModalOpen: false,
  isRecoverModalOpen: false,
  isShowTokenModalOpen: false,

  // Modal data
  twoFAQrCode: "",
  twoFASecret: "",
  recoveryCodes: [],
  newSessionToken: "",
  showNewToken: false,
  pendingSelfDestructMessage: "",

  // UI state
  appLocked: false,
  pinCheckDone: false,

  // Loading states
  is2FALoading: false,
  isPinLoading: false,
  isRecoveryLoading: false,
  notification: { show: false, message: "", type: "info" },
};

const ACTIONS = {
  // Modal toggles
  SET_PRIVATE_KEY_MODAL: "SET_PRIVATE_KEY_MODAL",
  SET_TWOFA_MODAL: "SET_TWOFA_MODAL",
  SET_CONTACT_MODAL: "SET_CONTACT_MODAL",
  SET_PIN_MODAL: "SET_PIN_MODAL",
  SET_SELF_DESTRUCT_MODAL: "SET_SELF_DESTRUCT_MODAL",
  SET_NEW_TOKEN_MODAL: "SET_NEW_TOKEN_MODAL",
  SET_RECOVERY_MODAL: "SET_RECOVERY_MODAL",
  SET_RECOVER_MODAL: "SET_RECOVER_MODAL",
  SET_SHOW_TOKEN_MODAL: "SET_SHOW_TOKEN_MODAL",

  // Modal data
  SET_TWOFA_DATA: "SET_TWOFA_DATA",
  SET_RECOVERY_CODES: "SET_RECOVERY_CODES",
  SET_NEW_SESSION_TOKEN: "SET_NEW_SESSION_TOKEN",
  SET_SHOW_NEW_TOKEN: "SET_SHOW_NEW_TOKEN",
  SET_PENDING_SELF_DESTRUCT: "SET_PENDING_SELF_DESTRUCT",

  // UI state
  SET_APP_LOCKED: "SET_APP_LOCKED",
  SET_PIN_CHECK_DONE: "SET_PIN_CHECK_DONE",

  // Loading states
  SET_2FA_LOADING: "SET_2FA_LOADING",
  SET_PIN_LOADING: "SET_PIN_LOADING",
  SET_RECOVERY_LOADING: "SET_RECOVERY_LOADING",

  // Notification
  SET_NOTIFICATION: "SET_NOTIFICATION",
  CLEAR_NOTIFICATION: "CLEAR_NOTIFICATION",
};

function uiSettingsReducer(state, action) {
  switch (action.type) {
    case ACTIONS.SET_PRIVATE_KEY_MODAL:
      return { ...state, isPrivateKeyModalOpen: action.payload };

    case ACTIONS.SET_TWOFA_MODAL:
      return { ...state, isTwoFAModalOpen: action.payload };

    case ACTIONS.SET_CONTACT_MODAL:
      return { ...state, isContactModalOpen: action.payload };

    case ACTIONS.SET_PIN_MODAL:
      return { ...state, isPinModalOpen: action.payload };

    case ACTIONS.SET_SELF_DESTRUCT_MODAL:
      return { ...state, isSelfDestructModalOpen: action.payload };

    case ACTIONS.SET_NEW_TOKEN_MODAL:
      return { ...state, isNewTokenModalOpen: action.payload };

    case ACTIONS.SET_RECOVERY_MODAL:
      return { ...state, isRecoveryModalOpen: action.payload };

    case ACTIONS.SET_RECOVER_MODAL:
      return { ...state, isRecoverModalOpen: action.payload };

    case ACTIONS.SET_SHOW_TOKEN_MODAL:
      return { ...state, isShowTokenModalOpen: action.payload };

    case ACTIONS.SET_TWOFA_DATA:
      return {
        ...state,
        twoFAQrCode: action.payload.qrCode,
        twoFASecret: action.payload.secret,
      };

    case ACTIONS.SET_RECOVERY_CODES:
      return { ...state, recoveryCodes: action.payload };

    case ACTIONS.SET_NEW_SESSION_TOKEN:
      return { ...state, newSessionToken: action.payload };

    case ACTIONS.SET_SHOW_NEW_TOKEN:
      return { ...state, showNewToken: action.payload };

    case ACTIONS.SET_PENDING_SELF_DESTRUCT:
      return { ...state, pendingSelfDestructMessage: action.payload };

    case ACTIONS.SET_APP_LOCKED:
      return { ...state, appLocked: action.payload };

    case ACTIONS.SET_PIN_CHECK_DONE:
      return { ...state, pinCheckDone: action.payload };

    case ACTIONS.SET_2FA_LOADING:
      return { ...state, is2FALoading: action.payload };

    case ACTIONS.SET_PIN_LOADING:
      return { ...state, isPinLoading: action.payload };

    case ACTIONS.SET_RECOVERY_LOADING:
      return { ...state, isRecoveryLoading: action.payload };

    case ACTIONS.SET_NOTIFICATION:
      return { ...state, notification: { show: true, ...action.payload } };

    case ACTIONS.CLEAR_NOTIFICATION:
      return {
        ...state,
        notification: { show: false, message: "", type: "info" },
      };

    default:
      return state;
  }
}

export function UISettingsProvider({ children }) {
  const [state, dispatch] = useReducer(uiSettingsReducer, initialState);
  const { user, updateTwoFAStatus, updatePinStatus, updateRecoveryCodesShown } =
    useAuth();

  const showNotification = useCallback(
    (message, type = "info", duration = 4000) => {
      dispatch({ type: ACTIONS.SET_NOTIFICATION, payload: { message, type } });
      setTimeout(() => {
        dispatch({ type: ACTIONS.CLEAR_NOTIFICATION });
      }, duration);
    },
    [],
  );

  // Modal setters
  const setIsPrivateKeyModalOpen = useCallback((open) => {
    dispatch({ type: ACTIONS.SET_PRIVATE_KEY_MODAL, payload: open });
  }, []);

  const setIsTwoFAModalOpen = useCallback((open) => {
    dispatch({ type: ACTIONS.SET_TWOFA_MODAL, payload: open });
  }, []);

  const setIsContactModalOpen = useCallback((open) => {
    dispatch({ type: ACTIONS.SET_CONTACT_MODAL, payload: open });
  }, []);

  const setIsPinModalOpen = useCallback((open) => {
    dispatch({ type: ACTIONS.SET_PIN_MODAL, payload: open });
  }, []);

  const setIsSelfDestructModalOpen = useCallback((open) => {
    dispatch({ type: ACTIONS.SET_SELF_DESTRUCT_MODAL, payload: open });
  }, []);

  const setIsNewTokenModalOpen = useCallback((open) => {
    dispatch({ type: ACTIONS.SET_NEW_TOKEN_MODAL, payload: open });
  }, []);

  const setIsRecoveryModalOpen = useCallback((open) => {
    dispatch({ type: ACTIONS.SET_RECOVERY_MODAL, payload: open });
  }, []);

  const setIsRecoverModalOpen = useCallback((open) => {
    dispatch({ type: ACTIONS.SET_RECOVER_MODAL, payload: open });
  }, []);

  const setIsShowTokenModalOpen = useCallback((open) => {
    dispatch({ type: ACTIONS.SET_SHOW_TOKEN_MODAL, payload: open });
  }, []);

  const setShowNewToken = useCallback((show) => {
    dispatch({ type: ACTIONS.SET_SHOW_NEW_TOKEN, payload: show });
  }, []);

  const setPendingSelfDestructMessage = useCallback((message) => {
    dispatch({ type: ACTIONS.SET_PENDING_SELF_DESTRUCT, payload: message });
  }, []);

  // 2FA Functions
  const setup2FA = useCallback(async () => {
    dispatch({ type: ACTIONS.SET_2FA_LOADING, payload: true });

    try {
      const data = await api.setup2FA();
      dispatch({
        type: ACTIONS.SET_TWOFA_DATA,
        payload: { qrCode: data.qrCode, secret: data.secret },
      });
      setIsTwoFAModalOpen(true);
    } catch (error) {
      showNotification("2FA setup failed: " + error.message, "error");
    } finally {
      dispatch({ type: ACTIONS.SET_2FA_LOADING, payload: false });
    }
  }, [showNotification, setIsTwoFAModalOpen]);

  const verify2FA = useCallback(
    async (code) => {
      dispatch({ type: ACTIONS.SET_2FA_LOADING, payload: true });

      try {
        await api.verify2FA(code);
        setIsTwoFAModalOpen(false);
        dispatch({
          type: ACTIONS.SET_TWOFA_DATA,
          payload: { qrCode: "", secret: "" },
        });
        updateTwoFAStatus(true);
        showNotification("2FA enabled successfully!", "success");
      } catch (error) {
        showNotification("2FA verification failed: " + error.message, "error");
      } finally {
        dispatch({ type: ACTIONS.SET_2FA_LOADING, payload: false });
      }
    },
    [updateTwoFAStatus, showNotification, setIsTwoFAModalOpen],
  );

  const disable2FA = useCallback(async () => {
    if (!window.confirm("Are you sure you want to disable 2FA?")) {
      return;
    }

    const code = prompt("Enter your 2FA code to disable:");
    if (!code || code.length !== 6) {
      showNotification("Valid 6-digit code required", "error");
      return;
    }

    dispatch({ type: ACTIONS.SET_2FA_LOADING, payload: true });

    try {
      await api.disable2FA(code);
      updateTwoFAStatus(false);
      showNotification("2FA disabled successfully", "success");
    } catch (error) {
      showNotification(error.message, "error");
    } finally {
      dispatch({ type: ACTIONS.SET_2FA_LOADING, payload: false });
    }
  }, [updateTwoFAStatus, showNotification]);

  // PIN Functions
  const setPin = useCallback(
    async (pin) => {
      dispatch({ type: ACTIONS.SET_PIN_LOADING, payload: true });

      try {
        await api.setPin(pin);
        updatePinStatus(true);
        setIsPinModalOpen(false);
        showNotification("PIN set successfully!", "success");
      } catch (error) {
        showNotification(error.message, "error");
      } finally {
        dispatch({ type: ACTIONS.SET_PIN_LOADING, payload: false });
      }
    },
    [updatePinStatus, showNotification, setIsPinModalOpen],
  );

  const verifyPin = useCallback(
    async (pin) => {
      dispatch({ type: ACTIONS.SET_PIN_LOADING, payload: true });

      try {
        await api.verifyPin(pin);
        dispatch({ type: ACTIONS.SET_APP_LOCKED, payload: false });
        setIsPinModalOpen(false);
        dispatch({ type: ACTIONS.SET_PIN_CHECK_DONE, payload: true });
        showNotification("App unlocked", "success");
      } catch (error) {
        showNotification(error.message, "error");
      } finally {
        dispatch({ type: ACTIONS.SET_PIN_LOADING, payload: false });
      }
    },
    [showNotification, setIsPinModalOpen],
  );

  const disablePin = useCallback(async () => {
    if (!window.confirm("Are you sure you want to disable PIN lock?")) {
      return;
    }

    dispatch({ type: ACTIONS.SET_PIN_LOADING, payload: true });

    try {
      await api.disablePin();
      updatePinStatus(false);
      showNotification("PIN disabled successfully", "success");
    } catch (error) {
      showNotification(error.message, "error");
    } finally {
      dispatch({ type: ACTIONS.SET_PIN_LOADING, payload: false });
    }
  }, [updatePinStatus, showNotification]);

  // Recovery Functions
  const fetchRecoveryCodes = useCallback(async () => {
    dispatch({ type: ACTIONS.SET_RECOVERY_LOADING, payload: true });

    try {
      const data = await api.getRecoveryCodes();
      dispatch({
        type: ACTIONS.SET_RECOVERY_CODES,
        payload: data.recoveryCodes,
      });
      setIsRecoveryModalOpen(true);
      updateRecoveryCodesShown(true);
    } catch (error) {
      showNotification(error.message, "error");
    } finally {
      dispatch({ type: ACTIONS.SET_RECOVERY_LOADING, payload: false });
    }
  }, [updateRecoveryCodesShown, showNotification, setIsRecoveryModalOpen]);

  const regenerateRecoveryCodes = useCallback(async () => {
    if (
      !window.confirm(
        "Regenerating codes will invalidate all previous recovery codes. Continue?",
      )
    ) {
      return;
    }

    dispatch({ type: ACTIONS.SET_RECOVERY_LOADING, payload: true });

    try {
      const data = await api.regenerateRecoveryCodes();
      dispatch({
        type: ACTIONS.SET_RECOVERY_CODES,
        payload: data.recoveryCodes,
      });
      showNotification("New recovery codes generated", "success");
    } catch (error) {
      showNotification(error.message, "error");
    } finally {
      dispatch({ type: ACTIONS.SET_RECOVERY_LOADING, payload: false });
    }
  }, [showNotification]);

  // Token Functions
  const fetchCurrentToken = useCallback(async () => {
    try {
      const data = await api.getSessionToken();
      dispatch({
        type: ACTIONS.SET_NEW_SESSION_TOKEN,
        payload: data.sessionToken,
      });
      setIsShowTokenModalOpen(true);
    } catch (error) {
      showNotification(error.message, "error");
    }
  }, [showNotification, setIsShowTokenModalOpen]);

  // Recovery Functions
  const recoverWithCode = useCallback(
    async (uuid, recoveryCode) => {
      dispatch({ type: ACTIONS.SET_RECOVERY_LOADING, payload: true });

      try {
        const data = await api.recoverWithCode(uuid, recoveryCode);
        dispatch({
          type: ACTIONS.SET_NEW_SESSION_TOKEN,
          payload: data.sessionToken,
        });
        setShowNewToken(true);
        showNotification(
          "Recovery successful! Use the new token to login.",
          "success",
        );
        return data.sessionToken;
      } catch (error) {
        showNotification(error.message, "error");
        return null;
      } finally {
        dispatch({ type: ACTIONS.SET_RECOVERY_LOADING, payload: false });
      }
    },
    [showNotification, setShowNewToken],
  );

  const recoverWithPassword = useCallback(
    async (uuid, password) => {
      dispatch({ type: ACTIONS.SET_RECOVERY_LOADING, payload: true });

      try {
        const data = await api.recoverWithPassword(uuid, password);
        dispatch({
          type: ACTIONS.SET_NEW_SESSION_TOKEN,
          payload: data.sessionToken,
        });
        setShowNewToken(true);
        showNotification(
          "Recovery successful! Use the new token to login.",
          "success",
        );
        return data.sessionToken;
      } catch (error) {
        showNotification(error.message, "error");
        return null;
      } finally {
        dispatch({ type: ACTIONS.SET_RECOVERY_LOADING, payload: false });
      }
    },
    [showNotification, setShowNewToken],
  );

  // Key rotation
  const rotateKeys = useCallback(
    async (currentPassword) => {
      if (!currentPassword) {
        showNotification("Password is required for key rotation.", "error");
        return false;
      }

      try {
        const { privateKey: newPrivateKey, publicKey: newPublicKey } =
          encryption.generateKeyPair();
        const data = await api.rotateKeys(newPublicKey, currentPassword);

        storage.savePrivateKey(user.uuid, newPrivateKey);
        encryption.setPrivateKey(newPrivateKey);
        showNotification("Keys rotated successfully!", "success");
        return true;
      } catch (error) {
        showNotification("Key rotation failed: " + error.message, "error");
        return false;
      }
    },
    [user?.uuid, showNotification],
  );

  // Event listeners for modals
  useEffect(() => {
    const handleOpenContactModal = () => setIsContactModalOpen(true);
    const handleOpenRecoverModal = () => setIsRecoverModalOpen(true);
    const handleShowRecoveryCodes = (event) => {
      dispatch({
        type: ACTIONS.SET_RECOVERY_CODES,
        payload: event.detail.codes,
      });
      dispatch({
        type: ACTIONS.SET_NEW_SESSION_TOKEN,
        payload: event.detail.token,
      });
      setIsRecoveryModalOpen(true);
    };

    const handleOpenPrivateKeyModal = () => setIsPrivateKeyModalOpen(true);
    window.addEventListener("openPrivateKeyModal", handleOpenPrivateKeyModal);

    window.addEventListener("openContactModal", handleOpenContactModal);
    window.addEventListener("openRecoverModal", handleOpenRecoverModal);
    window.addEventListener("showRecoveryCodes", handleShowRecoveryCodes);

    return () => {
      window.removeEventListener("openContactModal", handleOpenContactModal);
      window.removeEventListener("openRecoverModal", handleOpenRecoverModal);
      window.removeEventListener("showRecoveryCodes", handleShowRecoveryCodes);
      window.removeEventListener(
        "openPrivateKeyModal",
        handleOpenPrivateKeyModal,
      );
    };
  }, [setIsContactModalOpen, setIsRecoverModalOpen, setIsRecoveryModalOpen]);

  const value = {
    ...state,
    showNotification,
    setIsPrivateKeyModalOpen,
    setIsTwoFAModalOpen,
    setIsContactModalOpen,
    setIsPinModalOpen,
    setIsSelfDestructModalOpen,
    setIsNewTokenModalOpen,
    setIsRecoveryModalOpen,
    setIsRecoverModalOpen,
    setIsShowTokenModalOpen,
    setShowNewToken,
    setPendingSelfDestructMessage,
    setup2FA,
    verify2FA,
    disable2FA,
    setPin,
    verifyPin,
    disablePin,
    fetchRecoveryCodes,
    regenerateRecoveryCodes,
    fetchCurrentToken,
    recoverWithCode,
    recoverWithPassword,
    rotateKeys,
    dispatch,
    ACTIONS,
  };

  return (
    <UISettingsContext.Provider value={value}>
      {children}
    </UISettingsContext.Provider>
  );
}

export function useUISettings() {
  const context = React.useContext(UISettingsContext);
  if (!context) {
    throw new Error("useUISettings must be used within UISettingsProvider");
  }
  return context;
}
